import * as Sentry from '@sentry/react';
import { devtoolsExchange } from '@urql/devtools';
import { useTranslation } from 'react-i18next';
import { authExchange } from '@urql/exchange-auth';
import {
  Cache,
  cacheExchange,
  Data,
  FieldInfo,
  QueryInput,
  ResolveInfo,
  Resolver,
  ScalarObject,
  Variables,
} from '@urql/exchange-graphcache';
import { multipartFetchExchange } from '@urql/exchange-multipart-fetch';
import { retryExchange } from '@urql/exchange-retry';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { DocumentNode } from 'graphql';
// eslint-disable-next-line import/named
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { FC, useMemo } from 'react';
import {
  CombinedError,
  createClient,
  dedupExchange,
  errorExchange,
  Exchange,
  fetchExchange,
  gql,
  makeOperation,
  Provider,
  stringifyVariables,
} from 'urql';
import { pipe, share, subscribe } from 'wonka';
import { orderBy, isEqual } from 'lodash';

import { ViewableStages } from '../templates/work/JobTrackerTemplate/JobTrackerTemplate';
import { useAuth } from './AuthContext';
import { DEFAULT_JOBS_LIMIT } from '../templates/recruiter/lib';
import { updateSearchJobsCache } from './utils';
import {
  CandidateRecommender_CandidateRecommenderOutput,
  CandidateRecommender_EmbeddedCandidates,
  CandidateRecommender_RecommendedCandidates,
  CourseRecommender_CourseRecommenderOutput,
  Error_Code,
  GetAllAssessmentsDocument,
  GetRecruiterSavedTalentsByJobDocument,
  GetRecruiterSavedTalentsByJobQuery,
  GetRecruiterSavedTalentsDocument,
  GetRecruiterSavedTalentsQuery,
  GetSavedJobsByUserUuidDocument,
  GetUserProfileQueryVariables,
  JobRecommender_JobRecommenderOutput,
  JobService_JobDto,
  JobService_JobSearchV2ResponseWrapper,
  QueryGetCandidateRecommendationsArgs,
  QueryGetCourseRecommendationsArgs,
  QueryGetJobRecommendationsArgs,
  QueryGetResourceRecommendationsArgs,
  QuerySearchJobsV2Args,
  ResourceRecommender_ResourceRecommenderOutput,
  SaveTalentsMutation,
  SearchJobsV2Query,
  SearchJobsV2QueryVariables,
  UnsaveTalentsMutation,
  WithTypename,
  MutationUpsertAssessmentArgs,
  GraphCacheConfig,
  CourseRecommender_Course,
  GetTagsResponse,
  DeleteSavedJobMutation,
  GetAllAssessmentsQuery,
  GetSavedJobsByUserUuidQuery,
  JobRecommender_Job,
  Query,
  ResourceRecommender_Resource,
  SaveJobMutation,
  UpsertAssessmentMutation,
  GetUserCaseNotesQueryVariables,
} from '../generated-mesh/graphql';
import { JobSource } from '../utils/jobSource';

type DefaultPaginationResolver = Resolver<
  WithTypename<SearchJobsV2Query>,
  QuerySearchJobsV2Args,
  string | WithTypename<JobService_JobSearchV2ResponseWrapper>
>;

const defaultJobsPagination = (): DefaultPaginationResolver => {
  return (parent, fieldArgs, cache, info) => {
    const { parentKey: entityKey, fieldName: currentFieldName } = info;

    const allFields = cache.inspectFields(entityKey);
    const fieldInfos = allFields.filter((fieldInfo) => {
      const fieldInfoArgs = fieldInfo.arguments as QuerySearchJobsV2Args;

      return (
        fieldInfo.fieldName === currentFieldName &&
        fieldInfoArgs?.searchInput?.filters_array?.some(
          (filter) =>
            filter?.providers?.includes(JobSource.LINKUP) ||
            filter?.providers?.includes(JobSource.REVELIO)
        )
      );
    });

    const currentFieldKey = `${currentFieldName}(${stringifyVariables(
      fieldArgs
    )})`;

    const providers =
      fieldArgs?.searchInput?.filters_array
        ?.map((filter) => filter.providers)
        .flat() ?? [];

    if (
      !providers.includes(JobSource.LINKUP) &&
      !providers.includes(JobSource.REVELIO)
    ) {
      return {
        __typename: 'JobService_JobSearchV2ResponseWrapper',
        jobs: parent.searchJobsV2?.jobs,
        total_pages: parent.searchJobsV2?.total_pages,
        total_results: parent.searchJobsV2?.total_results,
      };
    }

    const isInCache = cache.resolve(
      cache.resolve(entityKey, currentFieldKey) as string,
      'jobs'
    );

    info.partial = !isInCache;

    let results: string[] = [];

    const currentFieldOffset = fieldArgs.searchInput.page as number;

    if (currentFieldOffset === 1) {
      const fieldInfoKey = cache.resolve(entityKey, currentFieldKey) as string;
      results = cache.resolve(fieldInfoKey, 'jobs') as string[];

      cache
        .inspectFields('Query')
        .filter((field) => field.fieldName === currentFieldName)
        .forEach((field) => {
          const hasDefaultProvider = (
            field.arguments as SearchJobsV2QueryVariables
          ).searchInput?.filters_array?.some(
            (filter) =>
              filter.providers?.includes(JobSource.LINKUP) ||
              filter.providers?.includes(JobSource.REVELIO)
          );

          if (field.fieldKey !== currentFieldKey && hasDefaultProvider) {
            cache.invalidate('Query', field.fieldKey);
          }
        });
    } else {
      fieldInfos.forEach((fieldInfo) => {
        const key = cache.resolve(entityKey, fieldInfo.fieldKey) as string;
        const fieldResults = cache.resolve(key, 'jobs') as string[];

        results.push(...fieldResults);
      });
    }

    return {
      __typename: 'JobService_JobSearchV2ResponseWrapper',
      jobs: results,
    } as any;
  };
};

type AwarePaginationResolver = <
  Args =
    | QueryGetCourseRecommendationsArgs
    | QueryGetJobRecommendationsArgs
    | QueryGetResourceRecommendationsArgs
    | QueryGetCandidateRecommendationsArgs,
  Output =
    | JobRecommender_JobRecommenderOutput
    | CourseRecommender_CourseRecommenderOutput
    | ResourceRecommender_ResourceRecommenderOutput
    | CandidateRecommender_CandidateRecommenderOutput
>(
  parent: WithTypename<Query>,
  args: Args,
  cache: Cache,
  info: ResolveInfo
) => string | (Output & { __typename?: string });

const recommenderPagination = ({
  offsetArgument = 'page',
  recommenderType = 'jobs',
}: {
  offsetArgument?: string;
  recommenderType?: 'jobs' | 'courses' | 'resources' | 'candidates';
}): AwarePaginationResolver => {
  return (_parent, fieldArgs, cache, info) => {
    const { parentKey: entityKey, fieldName: currentFieldName } = info;

    const allFields = cache.inspectFields(entityKey);
    const fieldInfos = allFields.filter(
      (fieldInfo) => fieldInfo.fieldName === currentFieldName
    );
    const size = fieldInfos.length;

    if (size === 0) {
      // if this is the first time querying (hasn't change pagination yet)
      return undefined as any;
    }

    const currentFieldKey = `${currentFieldName}(${stringifyVariables(
      fieldArgs
    )})`;

    const key = cache.resolve(entityKey, currentFieldKey) as string;
    const embeddedResult = cache.resolve(key, '_embedded') as any;

    const isInCache = cache.resolve(
      embeddedResult,
      recommenderType
    ) as string[];

    // when the query is being triggered, isInCache will be null until results came back
    info.partial = !isInCache;

    let results: string[] = [];
    let recommendedResults: string[] = [];

    const fieldQueryArgs = (fieldArgs as any).query
      ? (fieldArgs as any).query
      : (fieldArgs as {
          [key: string]: string | number;
        });
    const currentFieldOffset = fieldQueryArgs[offsetArgument] as number;

    if (currentFieldOffset === 1) {
      // invalidate cache if page = 1 (i.e. startover the accumulation of results, aka restart infinite pagination)
      const fieldInfoKey = cache.resolve(entityKey, currentFieldKey) as string;
      const embeddedResult = cache.resolve(fieldInfoKey, '_embedded') as any;
      results = cache.resolve(embeddedResult, recommenderType) as string[];

      if (recommenderType === 'candidates') {
        const recommendedResult = cache.resolve(
          fieldInfoKey,
          'recommended_candidates'
        ) as string | null;
        recommendedResults = cache.resolve(
          recommendedResult,
          recommenderType
        ) as string[];
      }

      cache
        .inspectFields('Query')
        .filter((field) => field.fieldName === currentFieldName)
        .forEach((field) => {
          // value of the limit variable of entry in the cache
          const fieldLimit = (field.arguments?.query as Variables)?.limit || 0;

          // value of the limit variable of the current query being executed
          const currentFieldLimit = fieldQueryArgs.limit as number;

          // Paginated queries have a limit of 20 while the one on home page has limit of 4
          //  we don't want to empty cached results of the latter when on find jobs.
          //  We also don't want the home page query to empty cached results of find jobs query.
          if (
            field.fieldKey !== currentFieldKey &&
            fieldLimit &&
            (fieldLimit as number) > 6 &&
            currentFieldLimit > 6
          ) {
            cache.invalidate('Query', field.fieldKey);
          }
        });
    } else {
      fieldInfos.forEach((fieldInfo) => {
        const key = cache.resolve(entityKey, fieldInfo.fieldKey) as string;
        // Only append cache data to the results list if the recommender params are still the same.
        // We should apply this validation logic to all recommender paginations, but we start with the buggy one first - course recommender (as it's used for different usecases - course recommendations & job posting's suggested courses. Without this validation logic, the latter usecase has a bug where wrong cache data from a different job posting were mistakenly appended to its results list when we view one job posting and then open another one).
        if (
          recommenderType === 'courses' &&
          !!(fieldInfo?.arguments?.input as any)?.parameters &&
          !!(fieldArgs as any)?.input?.parameters &&
          !isEqual(
            (fieldInfo?.arguments?.input as any)?.parameters,
            (fieldArgs as any)?.input?.parameters
          )
        ) {
          return;
        } else {
          const embeddedResult = cache.resolve(key, '_embedded') as any;
          const fieldResults = cache.resolve(
            embeddedResult,
            recommenderType
          ) as string[];
          results.push(...fieldResults);
          if (recommenderType === 'candidates') {
            const recommendedResult = cache.resolve(
              key,
              'recommended_candidates'
            ) as string | null;
            const recommendedCandidates = cache.resolve(
              recommendedResult,
              recommenderType
            ) as string[];
            recommendedResults.push(...recommendedCandidates);
          }
        }
      });
    }

    return (() => {
      switch (recommenderType) {
        case 'jobs':
          return {
            __typename: 'JobRecommender_JobRecommenderOutput',
            _embedded: {
              __typename: 'JobRecommender_EmbeddedJobs',
              jobs: results as unknown as JobRecommender_Job[],
            },
          };

        case 'courses':
          return {
            __typename: 'CourseRecommender_CourseRecommenderOutput',
            _embedded: {
              __typename: 'CourseRecommender_EmbeddedCourses',
              courses: results as unknown as CourseRecommender_Course[],
            },
          };

        case 'resources':
          return {
            __typename: 'ResourceRecommender_ResourceRecommenderOutput',
            _embedded: {
              __typename: 'ResourceRecommender_EmbeddedResources',
              resources: results as unknown as ResourceRecommender_Resource[],
            },
          };

        case 'candidates':
          return {
            __typename: 'CandidateRecommender_CandidateRecommenderOutput',
            _embedded: {
              __typename: 'CandidateRecommender_EmbeddedCandidates',
              candidates:
                results as unknown as CandidateRecommender_EmbeddedCandidates[],
            },
            recommended_candidates: {
              __typename: 'CandidateRecommender_RecommendedCandidates',
              candidates:
                recommendedResults as unknown as CandidateRecommender_RecommendedCandidates[],
            },
          };
      }
    })();
  };
};

const betterUpdateQuery = <Result, Query>(
  cache: Cache,
  queryInput: QueryInput,
  result: any,
  fn: (res: Result, query: Query) => Query
) => {
  return cache.updateQuery(
    queryInput,
    (data) => fn(result, data as any) as any
  );
};

const expirationThreshold = 5 * 60 * 1000; // 5 minutes in milliseconds

const didRequestErrorOnAuth = (error: CombinedError) =>
  error.graphQLErrors.some(
    // this is the code that is returned by the gateway when a token is invalid
    (e) => e.extensions?.code === Error_Code.UNAUTHORIZED
  );

export const getGqlString = (doc: DocumentNode): string | undefined =>
  doc.loc && doc.loc.source.body;

// sentryExchange shares the graphql query and payload with Sentry for
// debugging and error handling purposes.
const sentryExchange: Exchange = ({ forward }) => {
  return (ops$) => {
    const shared = pipe(ops$, share);
    pipe(
      shared,
      subscribe((query) => {
        Sentry.addBreadcrumb({
          category: 'graphql',
          message: `POST ${query.context.url}`,
          data: {
            graphql: getGqlString(query.query),
            variables: query.variables,
          },
          level: 'info',
        });
      })
    );
    return forward(shared);
  };
};

const invalidateCache = (cache: Cache, fieldNames: string[]) => {
  cache
    .inspectFields({
      __typename: 'Query',
    })
    .filter((field) => fieldNames.includes(field.fieldName))
    .forEach((field) => cache.invalidate('Query', field.fieldKey));
};

const createUrqlClient = ({
  getSession,
}: {
  getSession: () => Promise<CognitoUserSession | null>;
}) => {
  return createClient({
    url: import.meta.env.VITE_GRAPHQL_HOST || 'localhost',
    exchanges: [
      devtoolsExchange,
      dedupExchange,
      cacheExchange<GraphCacheConfig>({
        optimistic: {
          saveJob: (variables, cache, _info) => {
            const queryArgs = variables.save;
            const stage = queryArgs?.stage as ViewableStages;
            const jobUuid = queryArgs?.uuid;
            if (!jobUuid) {
              // when saving job for the first time
              return undefined;
            }

            const stageTypename = 'JobService_Stage';

            const job = cache.readFragment(
              gql`
                fragment _ on JobService_SavedJob {
                  __typename
                  company
                  created_at
                  description
                  job {
                    __typename
                    ingested_at
                    source
                    source_type
                    uuid
                  }
                  location
                  notes
                  stage_history {
                    __typename
                    created_at
                    stage
                  }
                  title
                  uuid
                }
              `,
              { uuid: jobUuid }
            );

            return {
              ...job,
              stage_history: [
                {
                  created_at: new Date().toISOString(),
                  stage,
                  __typename: stageTypename,
                },
                ...job.stage_history,
              ],
            };
          },
          deleteSavedJob: (variables, cache, _info) => {
            const jobUuid = variables.uuid;

            const job = cache.readFragment(
              gql`
                fragment _ on JobService_SavedJob {
                  __typename
                  company
                  created_at
                  description
                  job {
                    __typename
                    ingested_at
                    source
                    source_type
                    uuid
                  }
                  location
                  notes
                  stage_history {
                    __typename
                    created_at
                    stage
                  }
                  title
                  uuid
                }
              `,
              { uuid: jobUuid }
            );

            return job;
          },
          // TODO - currently if you try to save same candidate with different job_uuid, it throws an error - therefore commenting it out
          // saveTalents: (variables: any, cache: any, _info: any) => {
          //   const queryArgs = variables.body ? variables.body : variables;

          //   const talent_user_uuids = queryArgs?.input.talent_user_uuids;
          //   const user_uuid = queryArgs?.user_uuid;
          //   const job_uuid = queryArgs?.input.job_uuid;

          //   if (!user_uuid || !talent_user_uuids) {
          //     return;
          //   }

          //   return cache.readFragment(
          //     gql`
          //       fragment _ on SavedTalent {
          //         talent_uuid
          //         user_uuid
          //         job_uuid
          //         created_at
          //       }
          //     `,
          //     {
          //       talent_uuid: talent_user_uuids[0],
          //       job_uuid,
          //       // created_at: new Date(),
          //     }
          //   );
          // },
          // unsaveTalents: (variables: any, cache: any, _info: any) => {
          //   const queryArgs = variables.body ? variables.body : variables;
          //   const talent_user_uuids = queryArgs?.input.talent_user_uuids;
          //   const user_uuid = queryArgs?.user_uuid;
          //   const job_uuid = queryArgs?.input.job_uuid;

          //   if (!user_uuid || !talent_user_uuids) {
          //     return;
          //   }

          //   return cache.readFragment(
          //     gql`
          //       fragment _ on SavedTalent {
          //         talent_uuid
          //         user_uuid
          //         job_uuid
          //       }
          //     `,
          //     { talent_uuid: talent_user_uuids[0], job_uuid }
          //   );
          // },
        },
        updates: {
          Mutation: {
            addContact(_result, args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                // Only invalidate the cache for the user to whose profile the contact was added
                .filter(
                  (field) =>
                    field.fieldName === 'getUserProfile' &&
                    (
                      field.arguments
                        ?.getProfileInput as GetUserProfileQueryVariables
                    ).user_uuid === args.addContactInput.user_uuid
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });

              // Parse the url to check if user is on the
              // /jobs/:jobId route
              const url = new URL(window.location.href);
              const pathSegments = url.pathname.split('/');
              const jobsIndex = pathSegments.indexOf('jobs');
              const jobIdIndex =
                jobsIndex + 1 < pathSegments.length ? jobsIndex + 1 : -1;

              // If the user is on the /jobs/:jobId route, then
              // that means they are filtering talent by a specific job.
              // Only in this case will the previously_contacted field
              // be visible, thus we update the cache.
              if (jobsIndex > -1 && jobIdIndex > -1) {
                // Update the previously_contacted field on the candidate
                // to whom we added a contact to true. This allows us to
                // save a network request to getCandidateRecommendations (again)
                cache.writeFragment(
                  gql`
                    fragment _ on CandidateRecommender_Candidate {
                      previously_contacted
                    }
                  `,
                  {
                    id: args.addContactInput.user_uuid,
                    previously_contacted: true,
                  }
                );
              }

              // If user is not on that route, then previously_contacted
              // should always be false. Thus, we don't need to update the cache.
            },
            deleteResume(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'getPostObjectURL')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            updateTenantWorkConfiguration(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'getTenantConfiguration')
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            createCourse(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getCourse' ||
                    field.fieldName === 'getCourses'
                )
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            updateCourse(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getCourse' ||
                    field.fieldName === 'getCourses'
                )
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            toggleArchiveCourse(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getCourses' ||
                    field.fieldName === 'getCourse'
                )
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            createBusinessResource(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'getBusinessResources')
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            updateBusinessResource(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getBusinessResources' ||
                    field.fieldName === 'getBusinessResource'
                )
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            upsertUserNextStep(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getUserNextSteps' ||
                    // Next steps can also mutate assessments when completed
                    field.fieldName === 'getAllAssessments'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            deleteUserNextStep(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getUserNextSteps' ||
                    field.fieldName === 'getAllAssessments'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            createInviteLink(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'getInviteLinks')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            updateInviteLink(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'getInviteLinks')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            createReportExport(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getCombinedExportReports' ||
                    field.fieldName === 'getExportReports' ||
                    field.fieldName === 'getWeeklyUserProfilesReports'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            requestSubmissionsExportation(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'getExportReports')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            delNextStepDoc(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'downloadNextStepDoc')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            confirmNextStepDoc(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'downloadNextStepDoc')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            createUserTags(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getUserProfile' ||
                    field.fieldName === 'getUserProfiles' ||
                    field.fieldName === 'getAllUserUuidRole' ||
                    field.fieldName === 'getAllTags' ||
                    field.fieldName === 'getProfileFilters' ||
                    field.fieldName === 'getTags'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            upsertTag(result: any, args: any, cache: Cache, _info) {
              const tagId = args.upsertTagInput.tag_uuid;
              const isEditing = !!tagId;

              const rootQuery = {
                query: `
                  query GetTags($getTagsInput: GetTagsInput!) {
                    getTags(getTagsInput: $getTagsInput) {
                      items {
                        tag_uuid
                        label
                        status
                        category
                        tenant_uuid
                        updated_at
                        updated_by
                        created_at
                        created_by
                      }
                      count
                      next
                    }
                  }
                `,
              };

              const allCachedTagsQueries = cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter((field: FieldInfo) => field.fieldName === 'getTags');

              const allCachedTagsPages = allCachedTagsQueries.map(
                (field: FieldInfo) => ({
                  ...rootQuery,
                  variables: field.arguments?.query
                    ? field.arguments?.query
                    : field.arguments,
                })
              );

              if (!isEditing) {
                const unpagedField = allCachedTagsQueries.find(
                  (field: FieldInfo) => {
                    const { getTagsInput } = (field.arguments?.query ??
                      field.arguments) as ScalarObject;
                    return (
                      !Object.prototype.hasOwnProperty.call(
                        getTagsInput,
                        'skip'
                      ) &&
                      !Object.prototype.hasOwnProperty.call(
                        getTagsInput,
                        'take'
                      )
                    );
                  }
                );

                /**
                 * If we're adding a new tag and
                 * we have a getTags with no paging cached result
                 * then add it to the top of the list
                 *  */
                const unpagedQuery = {
                  ...rootQuery,
                  variables:
                    unpagedField?.arguments?.query ?? unpagedField?.arguments,
                };

                cache.updateQuery(unpagedQuery, (data) => {
                  if (!data || !unpagedQuery.variables) return data;

                  // Just add this new tag to the top of the page1
                  const newTagsPageData = data.getTags as GetTagsResponse;
                  newTagsPageData.items = [
                    result.upsertTag,
                    ...newTagsPageData.items,
                  ];

                  return {
                    getTags: newTagsPageData,
                    __typename: 'getTagsResponse',
                  } as Data;
                });
              } else {
                // Do an in-place replacement for that tag
                for (const queryWithVariablesArgs of allCachedTagsPages) {
                  cache.updateQuery(queryWithVariablesArgs, (data: any) => {
                    if (!data) return data;

                    const newTagsPageData: GetTagsResponse = data.getTags;
                    const tagIndex = newTagsPageData.items.findIndex(
                      ({ tag_uuid }) => tag_uuid === tagId
                    );

                    if (tagIndex === -1) {
                      return {
                        getTags: newTagsPageData,
                        __typename: 'getTagsResponse',
                      } as Data;
                    }

                    const argsInput = args.query
                      ? args.query
                      : args.upsertTagInput;

                    const mutatedTag = {
                      ...newTagsPageData.items[tagIndex],
                      ...argsInput,
                    };
                    // Just do an in-place update
                    newTagsPageData.items[tagIndex] = mutatedTag;
                    return {
                      getTags: newTagsPageData,
                      __typename: 'getTagsResponse',
                    } as Data;
                  });
                }
              }

              // Invalidate other results affected by this mutation
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getUserProfile' ||
                    field.fieldName === 'getUserProfiles' ||
                    field.fieldName === 'getAllUserUuidRole' ||
                    field.fieldName === 'getAllTags' ||
                    field.fieldName === 'getProfileFilters'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            updateUserProfile(_result, args, cache, _info) {
              const input = args.updateProfileInput;

              if (input?.tags) {
                cache
                  .inspectFields({ __typename: 'Query' })
                  .filter(
                    (field) =>
                      field.fieldName === 'getUserProfile' ||
                      field.fieldName === 'getUserProfiles' ||
                      field.fieldName === 'getAllUserUuidRole' ||
                      field.fieldName === 'getAllTags' ||
                      field.fieldName === 'getProfileFilters'
                  )
                  .forEach((field) => {
                    cache.invalidate('Query', field.fieldKey);
                  });
              }

              if (input?.target_career) {
                cache
                  .inspectFields({
                    __typename: 'Query',
                  })
                  .filter((field) => field.fieldName === 'getUserProfile')
                  .forEach((field) => {
                    cache.invalidate('Query', field.fieldKey);
                  });
              }

              if (input?.pref_lang || input?.is_onboarded) {
                // If the user changes their pref_lang or finishes onboarding, we invalidate the entire cache
                cache
                  .inspectFields({
                    __typename: 'Query',
                  })
                  .forEach((field) => {
                    cache.invalidate('Query', field.fieldKey);
                  });
              }

              if (
                input?.ethnicities ||
                input?.communitiesObject ||
                input?.employment_status
              ) {
                cache
                  .inspectFields({
                    __typename: 'Query',
                  })
                  .filter(
                    (field) =>
                      field.fieldName === 'getUserProfile' ||
                      field.fieldName === 'getUserProfiles' ||
                      field.fieldName === 'getAllUserUuidRole'
                  )
                  .forEach((field) => {
                    cache.invalidate('Query', field.fieldKey);
                  });
              }

              if (input?.location) {
                cache
                  .inspectFields({ __typename: 'Query' })
                  .filter(
                    (field) =>
                      field.fieldName === 'searchLmiCareers' ||
                      field.fieldName === 'getLmiCareersByIds'
                  )
                  .forEach((field) => {
                    cache.invalidate('Query', field.fieldKey);
                  });
              }
            },
            updateAccountPreferences(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'getUserProfile')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            inviteToExternalAssessment(_result, _args, cache, _info) {
              // Invalidate assessments and next steps results.
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getUserNextSteps' ||
                    field.fieldName === 'getAllAssessments'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            upsertAssessment(
              _result,
              args: MutationUpsertAssessmentArgs,
              cache,
              _info
            ) {
              const user_uuid = args?.upsertAssessmentInput?.user_uuid ?? '';

              betterUpdateQuery<
                UpsertAssessmentMutation,
                GetAllAssessmentsQuery
              >(
                cache,
                {
                  query: GetAllAssessmentsDocument,
                  variables: { user_uuid },
                },
                _result,
                (result, query) => {
                  // If we update an existing entity, do nothing -> let urql handle the rest
                  if (
                    query.getAllAssessments.some(
                      (assessment) =>
                        assessment.assessment_uuid ===
                        result.upsertAssessment.assessment_uuid
                    )
                  ) {
                    return query;
                  }

                  // If we add a new entity, then update the list for getAllAssessments query
                  return {
                    getAllAssessments: [
                      result.upsertAssessment,
                      ...query.getAllAssessments,
                    ],
                  };
                }
              );
            },
            saveJob(_result, args, cache, _info) {
              const queryArgs = args;
              const stage = queryArgs?.save?.stage!;

              const objectWithUserUuid = cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'savedJobsByUserUUID')[0]
                .arguments!;

              const userUuid = objectWithUserUuid.user_uuid;

              const savedJobsTypename = 'JobService_SavedJob';
              const stageTypename = 'JobService_Stage';

              betterUpdateQuery<SaveJobMutation, GetSavedJobsByUserUuidQuery>(
                cache,
                {
                  query: GetSavedJobsByUserUuidDocument,
                  variables: {
                    userUuid,
                  },
                },
                _result,
                (result, query) => {
                  if (!result.saveJob) {
                    return { savedJobsByUserUUID: query.savedJobsByUserUUID };
                  }
                  // saving the first time - no saved uuid
                  if (
                    !query.savedJobsByUserUUID.some(
                      (j) => j.job.source === result.saveJob.job.source
                    )
                  ) {
                    return {
                      savedJobsByUserUUID: [
                        ...query.savedJobsByUserUUID,
                        {
                          ...result.saveJob,
                          __typename: savedJobsTypename,
                        },
                      ],
                    };
                  }

                  // updating job stage - has a saved uuid
                  return {
                    savedJobsByUserUUID: query.savedJobsByUserUUID.map(
                      (job) => {
                        if (job.job.source === result.saveJob.job.source) {
                          return {
                            ...job,
                            stage_history: [
                              {
                                created_at: new Date().toISOString(),
                                stage,
                                __typename: stageTypename,
                              },
                              ...job.stage_history,
                            ],
                            __typename: savedJobsTypename,
                          };
                        } else return job;
                      }
                    ),
                  };
                }
              );
            },
            deleteSavedJob(_result, _args, cache, _info) {
              const objectWithUserUuid = cache
                .inspectFields({ __typename: 'Query' })
                .filter((field) => field.fieldName === 'savedJobsByUserUUID')[0]
                .arguments!;

              const userUuid = objectWithUserUuid.user_uuid;

              betterUpdateQuery<
                DeleteSavedJobMutation,
                GetSavedJobsByUserUuidQuery
              >(
                cache,
                {
                  query: GetSavedJobsByUserUuidDocument,
                  variables: {
                    userUuid,
                  },
                },
                _result,
                (result, query) => {
                  return {
                    savedJobsByUserUUID: query.savedJobsByUserUUID.filter(
                      (j) => j.uuid !== result.deleteSavedJob.uuid
                    ),
                  };
                }
              );
            },
            updateDraftRoadmap(_result, _args, cache, _info) {
              // If the user saves draft roadmap, we invalidate the roadmap cache
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter((field) => field.fieldName === 'getRoadmap')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            publishRoadmap(_result, _args, cache, _info) {
              // If the user publishes roadmap, we invalidate the roadmap cache
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter((field) => field.fieldName === 'getRoadmap')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            publishForm(_result, _args, cache, _info) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getForms' ||
                    field.fieldName === 'getOnboardingForm' ||
                    field.fieldName === 'getFormDetails' ||
                    field.fieldName === 'getDefaultQuestionsV2'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            addNextStepToLibrary(_result, _args, cache, _info) {
              // If the user saves to library roadmap, we invalidate the next steps library cache
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter((field) => field.fieldName === 'getNextStepsLibrary')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            assignCoach(_result, _args, cache) {
              // If the coach assigns users, we invalidate the profiles cache
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getUserProfiles' ||
                    field.fieldName === 'getAllUserUuidRole'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            assignGroupCoaches(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'findGroup' ||
                    field.fieldName === 'getGroups' ||
                    field.fieldName === 'getUserProfiles' ||
                    field.fieldName === 'getAllUserUuidRole'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            unassignGroupCoaches(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'findGroup' ||
                    field.fieldName === 'getGroups' ||
                    field.fieldName === 'getUserProfiles' ||
                    field.fieldName === 'getAllUserUuidRole'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            createEmployer(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getEmployers' ||
                    field.fieldName === 'getRecruiters'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            updateEmployer(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getEmployers' ||
                    field.fieldName === 'getRecruiters'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            resetEmployerAssociations(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'findEmployer' ||
                    field.fieldName === 'searchJobsV2'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            deleteEmployer(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getEmployers' ||
                    field.fieldName === 'getRecruiters'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            createRecruiter(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getRecruiters' ||
                    field.fieldName === 'getEmployers' ||
                    field.fieldName === 'getGroups' ||
                    field.fieldName === 'findGroup'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            updateRecruiter(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getRecruiters' ||
                    field.fieldName === 'getEmployers'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            deleteRecruiters(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getRecruiters' ||
                    field.fieldName === 'getEmployers' ||
                    field.fieldName === 'getGroups' ||
                    field.fieldName === 'findGroup'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            createGroup(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getEmployers' ||
                    field.fieldName === 'getGroups'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            updateGroup(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getEmployers' ||
                    field.fieldName === 'getGroups'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            removeUserSkills(_parent, args, cache) {
              cache.invalidate(
                {
                  __typename: 'Profile',
                  user_uuid: args.removeUserSkillsInput.userUUID,
                },
                'skills'
              );
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field: any) =>
                    field.fieldName === 'getJobRecommendationsByIds' ||
                    field.fieldName === 'GetCourseRecommendations'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            addUserSkills(_parent, args, cache) {
              cache.invalidate(
                {
                  __typename: 'Profile',
                  user_uuid: args.addUserSkillsInput.userUUID,
                },
                'skills'
              );
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field: any) =>
                    field.fieldName === 'getJobRecommendationsByIds' ||
                    field.fieldName === 'GetCourseRecommendations'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            updateUserSkills(_parent, args, cache) {
              cache.invalidate(
                {
                  __typename: 'Profile',
                  user_uuid: args.updateUserSkillsInput.userUUID,
                },
                'skills'
              );
            },
            addUserCaseNote(_result, args, cache, _info) {
              const userIdFromArgs = args.createUserCaseNoteInput.user_uuid;

              return cache
                .inspectFields({ __typename: 'Query' })
                .filter(({ fieldName, arguments: fieldArgs }) => {
                  const fieldArguments =
                    fieldArgs as GetUserCaseNotesQueryVariables;
                  const cachedQueryInput =
                    fieldArguments?.getUserCaseNotesInput;

                  return (
                    fieldName === 'getUserCaseNotes' &&
                    cachedQueryInput?.user_uuid === userIdFromArgs
                  );
                })
                .forEach(({ fieldKey }) => cache.invalidate('Query', fieldKey));
            },
            deleteUserCaseNote(_result, args, cache, _info) {
              const userIdFromArgs = args.deleteUserCaseNoteInput.user_uuid;

              return cache
                .inspectFields({ __typename: 'Query' })
                .filter(({ arguments: fieldArgs, fieldName }) => {
                  const fieldArguments =
                    fieldArgs as GetUserCaseNotesQueryVariables;
                  const cachedQueryInput =
                    fieldArguments?.getUserCaseNotesInput;

                  return (
                    fieldName === 'getUserCaseNotes' &&
                    cachedQueryInput?.user_uuid === userIdFromArgs
                  );
                })
                .forEach(({ fieldKey }) => cache.invalidate('Query', fieldKey));
            },
            saveTalents(_result, _args, cache, _info) {
              const userUuid = _args.user_uuid;
              const jobUuid = _args.input?.job_uuid;

              if (!userUuid) {
                return;
              }

              if (jobUuid) {
                betterUpdateQuery<
                  SaveTalentsMutation,
                  GetRecruiterSavedTalentsByJobQuery
                >(
                  cache,
                  {
                    query: GetRecruiterSavedTalentsByJobDocument,
                    variables: {
                      user_uuid: userUuid,
                      job_uuid: jobUuid,
                    },
                  },
                  _result,
                  (result, query) => {
                    const newEntries =
                      result?.saveTalents?.map((each) => ({
                        user_uuid: userUuid,
                        job_uuid: jobUuid,
                        talent_uuid: each.talent_uuid,
                        created_at: each.created_at,
                        __typename: 'SavedTalent' as const,
                      })) || [];
                    return {
                      getRecruiterSavedTalentsByJob:
                        query.getRecruiterSavedTalentsByJob?.concat(newEntries),
                    };
                  }
                );
              } else {
                betterUpdateQuery<
                  SaveTalentsMutation,
                  GetRecruiterSavedTalentsQuery
                >(
                  cache,
                  {
                    query: GetRecruiterSavedTalentsDocument,
                    variables: {
                      user_uuid: userUuid,
                    },
                  },
                  _result,
                  (result, query) => {
                    const newEntries =
                      result.saveTalents?.map((each) => ({
                        user_uuid: userUuid,
                        talent_uuid: each.talent_uuid,
                        created_at: each.created_at,
                        __typename: 'SavedTalent' as const,
                      })) || [];
                    return {
                      getRecruiterSavedTalents:
                        query.getRecruiterSavedTalents?.concat(newEntries),
                    };
                  }
                );
              }
            },
            unsaveTalents(_result, _args, cache, _info) {
              const userUuid = _args.user_uuid;
              const jobUuid = _args.input?.job_uuid;
              const talentUserUuids = _args.input?.talent_user_uuids;

              if (!userUuid) {
                return;
              }

              if (jobUuid) {
                betterUpdateQuery<
                  UnsaveTalentsMutation,
                  GetRecruiterSavedTalentsByJobQuery
                >(
                  cache,
                  {
                    query: GetRecruiterSavedTalentsByJobDocument,
                    variables: {
                      user_uuid: userUuid,
                      job_uuid: jobUuid,
                    },
                  },
                  _result,
                  (_result, query) => {
                    return {
                      getRecruiterSavedTalentsByJob:
                        query.getRecruiterSavedTalentsByJob?.filter(
                          (saved_talent) =>
                            !talentUserUuids?.includes(saved_talent.talent_uuid)
                        ),
                    };
                  }
                );
              } else {
                betterUpdateQuery<
                  UnsaveTalentsMutation,
                  GetRecruiterSavedTalentsQuery
                >(
                  cache,
                  {
                    query: GetRecruiterSavedTalentsDocument,
                    variables: {
                      user_uuid: userUuid,
                    },
                  },
                  _result,
                  (_result, query) => {
                    return {
                      getRecruiterSavedTalents:
                        query.getRecruiterSavedTalents?.filter(
                          (saved_talent) =>
                            !talentUserUuids?.includes(saved_talent.talent_uuid)
                        ),
                    };
                  }
                );
              }
            },
            updateTenant(_result, _args, cache, _info) {
              // If the user updates tenant, we invalidate the tenant cache
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter((field) => field.fieldKey === 'getCurrentUserTenant')
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            updateSubmission(_result, _args, cache, _info) {
              // If the user updates tenant, we invalidate the tenant cache
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) => field.fieldName === 'getFormAndLastSubmission'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            saveSubmission(_result, _args, cache, _info) {
              // If the user updates tenant, we invalidate the tenant cache
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) => field.fieldName === 'getFormAndLastSubmission'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            upsertUserRole(_result, _args, cache) {
              // When admin changes users' role (e.g. from Manage users table), we invalidate cache for queries that are related to users' roles. This ensures admins can have an up-to-date list of users with their correct roles, without refreshing the screen.
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getUserProfiles' ||
                    field.fieldName === 'getAllUserUuidRole' ||
                    field.fieldName === 'getAllCoaches' ||
                    field.fieldName === 'getGroups' ||
                    field.fieldName === 'findGroup'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            deleteUsers(_result, _args, cache, _info) {
              // If admins delete users, we invalidate the queries in Manage users table
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getUserProfiles' ||
                    field.fieldName === 'getAllUserUuidRole' ||
                    field.fieldName === 'getAllCoaches'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            updateDefaultQuestionsV2(_result, _args, cache, _info) {
              // If the user saves draft roadmap, we invalidate the roadmap cache
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter((field) => field.fieldName === 'getDefaultQuestionsV2')
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            updateFormVersion(_result, _args, cache) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getOnboardingForm' ||
                    field.fieldName === 'getDefaultQuestionsV2'
                )
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            createFormVersion(_result, _args, cache) {
              cache
                .inspectFields({ __typename: 'Query' })
                .filter(
                  (field) =>
                    field.fieldName === 'getOnboardingForm' ||
                    field.fieldName === 'getDefaultQuestionsV2'
                )
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            updateOnboardingFormActivationStatus(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getOnboardingForm' ||
                    field.fieldName === 'getFormDetails' ||
                    field.fieldName === 'getCurrentUserTenant'
                )
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            createDefaultQuestionsV2(_result, _args, cache, _info) {
              // If the user saves draft roadmap, we invalidate the roadmap cache
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter((field) => field.fieldName === 'getDefaultQuestionsV2')
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            updateConfiguration(_result, _args, cache, _info) {
              // If the user updates configuration, we invalidate the careers, resources, learning providers and configuration cache
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getCareers' ||
                    field.fieldName === 'getResources' ||
                    field.fieldName === 'getLearningProviders' ||
                    field.fieldName === 'getConfiguration'
                )
                .forEach((field) => cache.invalidate('Query', field.fieldKey));
            },
            createPostObjectURL(_result, _args, cache) {
              cache
                .inspectFields({
                  __typename: 'Query',
                })
                .filter(
                  (field) =>
                    field.fieldName === 'getUserProfile' ||
                    field.fieldName === 'getUserProfiles' ||
                    field.fieldName === 'getAllUserUuidRole' ||
                    field.fieldName === 'getWorkExperience' ||
                    field.fieldName === 'getWorkExperienceAndSkills' ||
                    field.fieldName === 'getPostObjectURL'
                )
                .forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
            },
            updateManualJob(result, args, cache, _info) {
              if (!result?.updateManualJob?.job) return;
              const { active: activeInput } = args?.updateManualJobInput || {};

              // the updateManualJobInput active = true = restore the archived job
              // we add the restore job back to the searchJob active job list and (filter is_active === true)
              // remove the restore active job from the searchjob archived job list (filter is_active === false)
              updateSearchJobsCache(cache, (data, field) => {
                const {
                  limit = DEFAULT_JOBS_LIMIT,
                  filters_array,
                  sort: { sort_by: sortByKey, is_ascending: isAscending },
                } = field?.arguments?.searchInput || {};

                // if not update the job active status = not restore the arrived job
                // we don't need to handle the cache of searchJobs result job list
                if (!activeInput) {
                  return data;
                }

                const isActive = filters_array?.[0]?.is_active; // is_active is same for each filters object in filters array
                const { jobDto: updatedJob = {} } =
                  result?.updateManualJob ?? {};
                const {
                  jobs: searchJobs,
                  total_results: searchJobsTotalResults,
                } = data?.searchJobsV2 || {};
                let jobs = [...searchJobs];
                let totalResults = searchJobsTotalResults;
                const findUpdatedJob = jobs.find(
                  (job) => job.job_id === updatedJob.job_id
                );

                // add the archived job back to active job list following the sort order
                if (isActive && !findUpdatedJob) {
                  jobs = orderBy(
                    [updatedJob, ...jobs],
                    [sortByKey],
                    [isAscending ? 'asc' : 'desc']
                  );
                  totalResults += 1;
                  if (jobs.length > limit) {
                    jobs.pop();
                  }
                }

                /**
                 * remove the restored job (active now) in the archive list if found
                 * is_active === undefined means no active filter
                 * is_active === false means querying archived jobs
                 */
                if (isActive === false && findUpdatedJob) {
                  jobs = jobs.filter((job) => job.job_id !== updatedJob.job_id);
                  const filteredJobsCount = searchJobs.length - jobs.length;
                  totalResults -= filteredJobsCount;
                }

                return {
                  searchJobsV2: {
                    ...data.searchJobsV2,
                    total_results: totalResults,
                    jobs,
                  },
                };
              });
            },
            createManualPostJob(result, _args, cache, _info) {
              if (!result?.createManualPostJob?.jobDto) return;
              updateSearchJobsCache(cache, (data, field) => {
                const {
                  limit = DEFAULT_JOBS_LIMIT,
                  page = 1,
                  search,
                } = field.arguments.searchInput || {};
                const { jobDto: createdJob = {} } =
                  result?.createManualPostJob ?? {};
                // no update the jobs cache when it is existed in the search job results or the query is not on the first page
                if (
                  data.searchJobsV2.jobs.some(
                    (job: JobService_JobDto) => job.job_id === createdJob.job_id
                  ) ||
                  page !== 1 ||
                  search
                ) {
                  return data;
                }

                const jobs = [createdJob, ...data.searchJobsV2.jobs];

                if (jobs.length > limit) {
                  // if the job array exceed the page limit
                  jobs.pop();
                }
                // cache the total result counts
                const total_results = data.searchJobsV2.total_results + 1;
                return {
                  searchJobsV2: {
                    ...data.searchJobsV2,
                    total_pages: Math.ceil(total_results / DEFAULT_JOBS_LIMIT),
                    total_results,
                    jobs,
                  },
                };
              });
            },
            archiveManualJob(result, _args, cache, _info) {
              if (!result?.archiveManualJob?.job) return;
              updateSearchJobsCache(cache, (data, field) => {
                const {
                  limit = DEFAULT_JOBS_LIMIT,
                  filters_array,
                  sort: { sort_by: sortByKey, is_ascending: isAscending },
                } = field?.arguments?.searchInput || {};

                const isActive = filters_array?.[0].is_active;
                const { jobDto: archivedJob = {} } = result.archiveManualJob;
                const {
                  jobs: searchJobs,
                  total_results: searchJobsTotalResults,
                } = data?.searchJobsV2 || {};
                let jobs = [...searchJobs];
                let totalResults = searchJobsTotalResults;
                const findArchivedJob = jobs.find(
                  (job) => job.job_id === archivedJob.job_id
                );

                // remove the archived job from the active query result
                if (isActive && findArchivedJob) {
                  jobs = jobs.filter(
                    (job) => job.job_id !== archivedJob.job_id
                  );
                  const filteredJobsCount =
                    data.searchJobsV2.jobs.length - jobs.length; // in case no job has been filtered.
                  totalResults -= filteredJobsCount;
                }

                // add the archived job to the archived query result
                if (isActive === false && !findArchivedJob) {
                  jobs = orderBy(
                    [archivedJob, ...jobs],
                    [sortByKey],
                    [isAscending ? 'asc' : 'desc']
                  );
                  totalResults += 1;
                  if (jobs.length > limit) {
                    jobs.pop();
                  }
                }

                return {
                  searchJobsV2: {
                    ...data.searchJobsV2,
                    total_results: totalResults,
                    jobs,
                  },
                };
              });
            },
            unassignCoach(_result, _args, cache) {
              invalidateCache(cache, ['getUserProfiles']);
            },
            moveUsers(_result, _args, cache) {
              invalidateCache(cache, ['getUserProfiles']);
            },
            bulkUpdateProfiles(_result, _args, cache) {
              invalidateCache(cache, ['getUserProfiles']);
            },
            saveResource(_result, _args, cache, _info) {
              invalidateCache(cache, [
                'searchResources',
                'getResource',
                'getSavedResourcesStatus',
                'getSavedResource',
              ]);
            },
            unsaveResource(_result, _args, cache, _info) {
              invalidateCache(cache, [
                'searchResources',
                'getResource',
                'getSavedResourcesStatus',
                'getSavedResource',
              ]);
            },
            saveLearning(_result, _args, cache, _info) {
              invalidateCache(cache, [
                'getCourses',
                'getCourse',
                'getSavedLearningsStatus',
                'getSavedLearning',
              ]);
            },
            unsaveLearning(_result, _args, cache, _info) {
              invalidateCache(cache, [
                'getCourses',
                'getCourse',
                'getSavedLearningsStatus',
                'getSavedLearning',
              ]);
            },
          },
        },
        resolvers: {
          Query: {
            searchJobsV2: defaultJobsPagination(),
            getCourseRecommendations: recommenderPagination({
              recommenderType: 'courses',
            }),
            getResourceRecommendations: recommenderPagination({
              recommenderType: 'resources',
            }),
            getCandidateRecommendations: recommenderPagination({
              recommenderType: 'candidates',
            }),
          },
        },
        keys: {
          Configuration: (data) => data.tenant_id!,
          Contact: (data) => data.contact_uuid!,
          Filters: () => null,
          GenereateURLReponse: () => null,
          MonetaryValue: () => null,
          JobsClassifierResponse: (data) => data.uuid!,
          ProfilesQueryWrapper: () => null,
          SkillClassifierResponse: (data) => data.uuid!,
          Course: (data) => data.uuid!,
          CareerRecommender_CareerRecommenderOutput: () => null,
          CourseRecommender_CourseRecommenderOutput: () => null,
          Link: () => null,
          Profile: (data) => data.user_uuid!,
          UserProfileRole: (data) => data.user_uuid!,
          ResourceRecommender_ResourceRecommenderOutput: () => null,
          Skill: (data) => data.id! ?? data.skill_uuid!,
          UserSkill: (data: any) => data.skill_uuid && data.skill_label,
          UserNextStepsWrapper: () => null,
          GetAllUserUuidRoleWrapper: () => null,
          UserNextSteps: (data) => data.next_step_uuid!,
          WorkExperience: (data) => data.work_exp_uuid!,
          EducationExperience: (data) => data.education_exp_uuid!,
          Certification: (data) => data.certification_uuid!,
          Role: () => null,
          Label: () => null,
          NextStepsMetadata: () => null,
          Assessment: (data) => data.assessment_uuid!,
          Tag: (data) => data.tag_uuid!,
          UserTag: (data) => data.tag_uuid!,
          GetTagsResponse: () => null,
          TargetCareer: (data) => data.career_uuid!,
          InterestProfile: () => null,
          SaveCareerStatusOutput: (data) => data.careerUuid!,
          SaveLearningStatusOutput: (data) => data.learningUuid!,
          SaveResourceStatusOutput: (data) => data.resourceUuid!,
          Service: (data) => data.service_uuid!,
          EmployedOutcome: () => null,
          ReferralOutcome: () => null,
          EducationOutcome: () => null,
          Outcomes: () => null,
          FormResponse: (data) => data.form_id!,
          NextStepDocDownload: (data) => data.s3Key!,
          DownloadNextStepDocsResponse: () => null,
          InviteLink: () => null,
          MultiChoiceCheckboxInputFieldOption: () => null,
          GenerateURLResponseNextStepDoc: () => null,
          PresignedPost: () => null,
          JobService_SavedJob: (data) => data.uuid!,
          JobService_JobDto: (data) => data.job_id!,
          JobService_JobSearchSkill: (data) => data.skill_uuid!,
          JobService_JobSearchJob: (data) => data.job_id!,
          JobService_JobSearchCareer: (data) => data.career_uuid!,
          JobService_JobSearchV2ResponseWrapper: () => null,
          JobService_GetJobsResponseWrapper: () => null,
          JobService_GetJobsV2ResponseWrapper: () => null,
          JobService_CompanySearchOutput: () => null,
          JobService_Company: () => null,
          JobService_AdditionalSkill: (data) =>
            `${data.skill_uuid}-${data.custom_label}`, // we allow users add different custom label with the same classified skill (same skill uuid)
          CandidateRecommender_CandidateRecommenderOutput: () => null,
          CandidateRecommender_EmbeddedCandidates: () => null,
          CandidateRecommender_RecommendedCandidates: () => null,
          CandidateRecommender_Candidate: (data) => data.id!,
          JobService_Stage: () => null,
          JobRecommender_JobRecommenderOutput: () => null,
          JobRecommender_Job: (data) => data.job_id!,
          FormVersionToFormBuilderResponse: () => null,
          InputField: () => null,
          FixedText: (data) => data.uuid!,
          DropdownInputField: () => null,
          DropdownInputFieldOption: () => null,
          DropdownInputFieldUserResponse: () => null,
          TextInputField: () => null,
          TextInputFieldUserResponse: () => null,
          MultiChoiceCheckboxInputField: () => null,
          MultiChoiceCheckboxInputFieldUserResponse: () => null,
          FormsListItem: () => null,
          CoachAssigned: (data) => data.coach_uuid!,
          CoachData: () => null,
          TenantRole: () => null,
          Feature: (data) => data.feature_uuid!,
          Employer: (data) => data.employer_uuid || null,
          Recruiter: (data) => data.recruiter_uuid || null,
          Invitation: (data) => data.invitation_uuid || null,
          RecruiterEmployerReference: () => null,
          RecruiterResponse: (data) => data.recruiter_uuid || null,
          EmployerSearchResult: () => null,
          UserReference: (data) => data.user_uuid || null,
          RegistrationForm: (data) => data.form_id || null,
          RegistrationInputField: () => null,
          DropdownInputFieldWithTags: (data) => data.uuid || null,
          MultiChoiceCheckboxInputFieldWithTags: (data) => data.uuid || null,
          DropdownInputFieldOptionWithTags: () => null,
          MultiChoiceCheckboxInputFieldOptionWithTags: () => null,
          SelectedMultiChoiceCheckboxInputFieldOptionWithTags: () => null,
          FormBuilderTag: (data) => data.uuid || null,
          RecruiterSearchResult: () => null,
          CaseNoteEditor: (data) => data.user_uuid!,
          GetUserCaseNotesResponse: () => null,
          SavedTalent: () => null,
          CareerArea: (data) => data.id || null,
          Career: () => null,
          Careers: () => null,
          GetCareersOutput: () => null,
          FilterOptions: () => null,
          DefaultQuestionField: () => null,
          DefaultQuestionsResponse: (data) => data.uuid || null,
          DefaultQuestionOptionWithTags: () => null,
          MultiChoiceCheckboxInputFieldUserResponseWithTags: () => null,
          DefaultQuestionGraphInputField: (data) => data.uuid || null,
          FormAndLastSubmissionResponse: () => null,
          DefaultQuestionsMultiChoiceInputFieldUserResponseWithTags: () => null,
          DataSourceType: () => null,
          MarketingConsentHistory: () => null,
          CourseRecommender_EmbeddedCourses: () => null,
        },
      }),
      authExchange<string>({
        addAuthToOperation: ({ authState, operation }) => {
          if (!authState) {
            return operation;
          }

          const fetchOptions =
            typeof operation.context.fetchOptions === 'function'
              ? operation.context.fetchOptions()
              : operation.context.fetchOptions || {};

          return makeOperation(operation.kind, operation, {
            ...operation.context,
            fetchOptions: {
              ...fetchOptions,
              headers: {
                ...fetchOptions.headers,
                Authorization: `Bearer ${authState}`,
              },
            },
          });
        },
        didAuthError: ({ error }) => {
          return didRequestErrorOnAuth(error);
        },
        // Preemptively refresh the token when it's close to expiring;
        //
        // This avoid problems where an API call will communicate with multiple services
        // and our token is on the verge of expiring.  In this scenario, it's possible one
        // API call succeeds and another fails, leaving our data in an inconsistent state.
        willAuthError: ({ authState }) => {
          // if there's no auth state then there's no expiry to check so
          // return false to indicate we don't know if this will error or not
          if (!authState) {
            return false;
          }

          const decodedToken = jwtDecode<JwtPayload>(authState);
          // if we can't read the token then there's no ability to check
          // the expiration date so proceed
          if (!decodedToken || decodedToken.exp === undefined) {
            console.error(
              'Unable to decode user auth token when evaluating expiry time.'
            );
            return false;
          }

          const tokenExpiration = decodedToken.exp * 1000; // Convert to milliseconds
          const currentTime = Date.now();

          const isTokenCloseToExpiring =
            tokenExpiration - currentTime <= expirationThreshold;

          // avoid scenarios where we might end up with a partial update in the backend
          if (isTokenCloseToExpiring) {
            console.warn('Token is close to expiring so refreshing.');
            return true;
          }

          return false;
        },
        getAuth: async () => {
          let session;
          try {
            session = await getSession();
          } catch (e) {
            // do nothing
          }

          return session?.getAccessToken().getJwtToken() || '';
        },
      }),
      retryExchange({
        maxNumberAttempts: 4,
        retryIf: (error, operation) => {
          // There's no point retrying if we had an auth error.  We'll need
          // to refresh our token via didAuthError first.
          if (didRequestErrorOnAuth(error)) {
            return false;
          }

          const operationName = (operation.query.definitions[0] as any)
            .selectionSet.selections[0].name.value;

          const EMPTY_RESPONSE_ERROR =
            'Template transformation yielded an empty response.';

          if (
            operationName === 'getUserProfile' ||
            operationName === 'updateUserProfile' ||
            operationName === 'getJobRecommendations' ||
            error.message.includes(EMPTY_RESPONSE_ERROR)
          ) {
            return !!(error.graphQLErrors.length > 0 || error.networkError);
          } else {
            return !!error.networkError;
          }
        },
      }),
      sentryExchange,
      errorExchange({
        onError(error) {
          Sentry.captureException(error);
        },
      }),
      multipartFetchExchange,
    ],
    maskTypename: true,
  });
};

const createUnauthenticatedUrqlClient = () => {
  return createClient({
    url: import.meta.env.VITE_GRAPHQL_HOST || 'localhost',
    fetchOptions: {
      headers: { 'x-api-key': import.meta.env.VITE_AWS_API_KEY! },
    },
    exchanges: [
      devtoolsExchange,
      dedupExchange,
      sentryExchange,
      errorExchange({
        onError(error) {
          Sentry.captureException(error);
        },
      }),
      fetchExchange,
    ],
  });
};

export const GraphProvider: FC = ({ children }) => {
  const { i18n } = useTranslation();
  const { user, getSession } = useAuth();

  //i18n.language added to dependency so that whenever we change languages, we are creating a new Urql cache in order to prevent stale/old translations in previous cache from appearing. Key to persistent and correct translations across app, please do not remove.
  const urqlClient = useMemo(() => {
    if (!user.token) {
      return createUnauthenticatedUrqlClient();
    }
    return createUrqlClient({ getSession });
  }, [user, getSession, i18n.language]);

  return <Provider value={urqlClient}>{children}</Provider>;
};
